import cn from 'classnames';
import { FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';

import { Button } from '@/shared/ui';

import styles from './MPRAddToReport.module.scss';

type MPRAddToReportProps = {
  attachTarget: Element | null;
  isLoading: boolean;
  isDisabled?: boolean;
  className?: string;
  showFrame?: boolean;
  onAddToReport: () => void;
};

export const MPRAddToReport: FC<MPRAddToReportProps> = (props) => {
  const {
    attachTarget,
    isLoading,
    isDisabled,
    className,
    showFrame = true,
    onAddToReport,
  } = props;

  const { formatMessage } = useIntl();

  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!attachTarget) {
      setIsHovered(false);
      return;
    }

    setIsHovered(attachTarget.matches(':hover'));

    const handleEnter = () => setIsHovered(true);
    const handleLeave = () => setIsHovered(false);

    attachTarget.addEventListener('mouseenter', handleEnter);
    attachTarget.addEventListener('mouseleave', handleLeave);

    return () => {
      attachTarget.removeEventListener('mouseenter', handleEnter);
      attachTarget.removeEventListener('mouseleave', handleLeave);
    };
  }, [attachTarget]);

  if (!attachTarget) {
    return null;
  }

  return createPortal(
    <div
      data-component="add-mpr-to-report-button"
      className={cn(
        styles.self,
        showFrame && styles.framed,
        isHovered && styles.hovered,
        className,
      )}
    >
      <Button
        size="small"
        loading={isLoading}
        disabled={isDisabled}
        onClick={(event) => {
          event.stopPropagation();
          onAddToReport();
        }}
      >
        {formatMessage({
          id: 'mpr.button.addToReport',
          defaultMessage: 'Add to report',
        })}
      </Button>
    </div>,
    attachTarget,
  );
};
